import { BarChartWrapper } from '@modules/charts/components/BarChartWrapper';
import { Bar } from '@modules/charts/components/BarChartWrapper/Bar';
import { ChartTooltipWrapperProvider } from '@modules/charts/components/ChartTooltipWrapper/ChartTooltipWrapperProvider';
import {
  BarChartLabelWidthUnit,
  BarChartXAxisProps,
  BarChartYAxisProps,
} from '@modules/charts/types';
import { getBarChartDomainFromMinMaxValues } from '@modules/charts/utils';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { formatNumToCompactNotation } from '@modules/common/utils';
import { CompareChartTooltip } from '@modules/quantify/components/CompareChartTooltip';
import { CompareBarChartXAxisLabel } from '@modules/quantify/components/QuantifyChart/CompareBarChartWrapper/CompareBarChartXAxisLabel';
import { useGetChartLabelOptions } from '@modules/quantify/hooks';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { CompareReportDataPoint } from '@modules/quantify/store/types';
import { getQuantifyBarLabelWidthBuffer } from '@modules/quantify/utils';
import { Stack, SxProps, Theme } from '@mui/material';
import pluralize from 'pluralize';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

export interface CompareBarChartProps {
  data: CompareReportDataPoint[];
  shouldShowAddToFilter?: boolean;
  shouldShowAddToFilterForBothCompareFields?: boolean;
  shouldDisableInteraction?: boolean;
  chartHeight?: number;
  xAxis?: BarChartXAxisProps<CompareReportDataPoint>;
  yAxis?: BarChartYAxisProps<CompareReportDataPoint>;
  barLabelSxProps?: SxProps<Theme>;
  shouldShowLargeView?: boolean;
  onResizeComplete?: (labelWidthPercentage: number) => void;
  defaultLabelWidth?: number;
  defaultLabelWidthUnits?: BarChartLabelWidthUnit;
  sxProps?: SxProps<Theme>;
  shouldTakeFullHeight?: boolean;
}

export const CompareBarChart = ({
  data,
  shouldDisableInteraction,
  shouldShowAddToFilter,
  shouldShowAddToFilterForBothCompareFields,
  xAxis,
  barLabelSxProps: barLabelSxPropsProp,
  shouldShowLargeView,
  onResizeComplete,
  defaultLabelWidth,
  sxProps,
  defaultLabelWidthUnits,
  shouldTakeFullHeight,
}: CompareBarChartProps) => {
  const { colorA, colorB } = useGetCompareColors();
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const formulaID = useQuantifyStore(({ query }) => query.formulaID);
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const getLabelOptions = useGetChartLabelOptions({ data });

  const xAxisDomain = ((): [number, number] => {
    if (xAxis?.domain) {
      return xAxis.domain;
    }

    const values = data.reduce((sum: number[], dataPoint) => {
      return [...sum, dataPoint.prevValue, dataPoint.currValue];
    }, []);

    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);

    return getBarChartDomainFromMinMaxValues({
      min: minValue,
      max: maxValue,
    });
  })();

  const getBarSxProps = (value: number): SxProps<Theme> => {
    return value >= 0
      ? {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderRadius: '2px',
        }
      : {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderRadius: '2px',
        };
  };

  const barLabelSxProps: SxProps<Theme> = {
    fontSize: '0.775rem',
    ...barLabelSxPropsProp,
  };

  const formatBarValueLabel = (value: number): string => {
    return formulaID === EQLFunctionName.NormaliseByPercent
      ? `${value}%`
      : String(formatNumToCompactNotation({ value }));
  };

  return (
    <BarChartWrapper
      data={data}
      xAxis={{
        dataKey: 'prevValue', // won't be used, but required for BarChartWrapperV2
        label: <CompareBarChartXAxisLabel />,
        domain: xAxisDomain,
        getLabelWidthBuffer: (dataPoint) =>
          getQuantifyBarLabelWidthBuffer({
            dataPoint,
            data,
          }),
      }}
      yAxis={{
        dataKey: 'label',
        label: !!groupBy?.detail?.metadata
          ? getFormattedDisplayNameForEqlField({
              eqlField: groupBy,
              shouldRemoveFileUploadPrefix,
              isUnifiedFieldEnabled,
            })
          : pluralize(groupBy?.displayName ?? ''),
      }}
      minHeight={500}
      shouldTakeFullHeight={shouldTakeFullHeight}
      shouldDisableInteraction={shouldDisableInteraction}
      getLabelOptions={getLabelOptions}
      defaultLabelWidth={defaultLabelWidth}
      defaultLabelWidthUnits={defaultLabelWidthUnits}
      onResizeComplete={onResizeComplete}
      sxProps={sxProps}
      dataRoleAttribute="compare-bar-chart-wrapper"
      renderBar={({ dataPoint, index, onMouseEnter, onMouseLeave }) => {
        return (
          <Stack height="80%" flex={1}>
            <Bar
              dataPoint={dataPoint}
              index={index}
              xAxis={{
                dataKey: 'currValue',
                domain: xAxisDomain,
              }}
              color={colorA}
              barHeight="100%"
              barSxProps={getBarSxProps(dataPoint.prevValue)}
              labelSxProps={barLabelSxProps}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              formatBarValueLabel={formatBarValueLabel}
              barRoleAttribute="compare-bar-chart-bar-a"
            />
            <Bar
              dataPoint={dataPoint}
              index={index}
              xAxis={{
                dataKey: 'prevValue',
                domain: xAxisDomain,
              }}
              color={colorB}
              barHeight="100%"
              barSxProps={getBarSxProps(dataPoint.currValue)}
              labelSxProps={barLabelSxProps}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              formatBarValueLabel={formatBarValueLabel}
              barRoleAttribute="compare-bar-chart-bar-b"
            />
          </Stack>
        );
      }}
      renderTooltipContent={({
        showTooltip,
        dataIndex,
        tooltipPosition,
        setShowTooltip,
        setTooltipPosition,
      }) => {
        const tooltipData = data[dataIndex ?? 0];

        return (
          <ChartTooltipWrapperProvider>
            <CompareChartTooltip
              payload={tooltipData}
              showTooltip={showTooltip}
              tooltipPosition={tooltipPosition}
              setShowTooltip={setShowTooltip}
              setTooltipPosition={setTooltipPosition}
              getLabelOptions={getLabelOptions}
              shouldShowAddToFilter={shouldShowAddToFilter}
              shouldShowAddToFilterForBothCompareFields={
                shouldShowAddToFilterForBothCompareFields
              }
            />
          </ChartTooltipWrapperProvider>
        );
      }}
    />
  );
};
